import type { PlanejamentoControle } from "@/modules/financeiro-obras";
import {
  EXCEDIDO_LABEL,
  EXCEDIDO_ORIENTACAO,
  KPI_CP,
  KPI_NC,
  KPI_TOTAL,
  NO_VALUE,
  OUTLOOK_CARD_FACILIDADES,
  OUTLOOK_CARD_MATERIAIS,
  OUTLOOK_CARD_SERVICOS,
  OUTLOOK_CARD_TOTAL_OBRA,
} from "./planejamento-controle.constants";
import { formatMilhoes, formatPercentual } from "./planejamento-controle.format";
import type { OutlookCardDados, OutlookKpiDados } from "./outlook-card/types-outlook-card";

interface Agrupamento {
  outlook: number;
  naoComprometido: number;
  comprometido: number;
}

interface PercentuaisCard {
  nc: number | null;
  cp: number | null;
}

type ParteKpi = "total" | "nc" | "cp";

function kpi(
  chave: string,
  parte: ParteKpi,
  rotulo: string,
  valor: string,
  extra: Partial<Pick<OutlookKpiDados, "complemento" | "excedido" | "orientacao">> = {},
): OutlookKpiDados {
  const testId = `outlook-card-${chave}-${parte}`;

  return {
    testId,
    valorTestId: `${testId}-valor`,
    rotulo,
    valor,
    complemento: null,
    excedido: false,
    orientacao: null,
    ...extra,
  };
}

function montarCard(
  chave: string,
  titulo: string,
  agrupamento: Agrupamento | null,
  percentuais: PercentuaisCard,
): OutlookCardDados {
  const testId = `outlook-card-${chave}`;
  if (!agrupamento) {
    return {
      chave,
      testId,
      titulo,
      total: kpi(chave, "total", KPI_TOTAL, NO_VALUE),
      nc: kpi(chave, "nc", KPI_NC, NO_VALUE),
      cp: kpi(chave, "cp", KPI_CP, NO_VALUE),
    };
  }

  const { outlook, naoComprometido, comprometido } = agrupamento;
  const acima = percentuais.nc === null ? null : Math.abs(percentuais.nc);

  return {
    chave,
    testId,
    titulo,
    total: kpi(chave, "total", KPI_TOTAL, formatMilhoes(outlook)),
    nc:
      naoComprometido < 0
        ? kpi(chave, "nc", KPI_NC, EXCEDIDO_LABEL, {
            complemento: `R$ ${formatMilhoes(Math.abs(naoComprometido))} (${formatPercentual(acima)} acima)`,
            excedido: true,
            orientacao: EXCEDIDO_ORIENTACAO,
          })
        : kpi(chave, "nc", KPI_NC, formatPercentual(percentuais.nc), {
            complemento: `(${formatMilhoes(naoComprometido)})`,
          }),
    cp: kpi(chave, "cp", KPI_CP, formatPercentual(percentuais.cp), {
      complemento: `(${formatMilhoes(comprometido)})`,
    }),
  };
}

type Categoria = "total" | "servicos" | "materiais" | "facilidades";

export function montarCards({ totais, linhas }: PlanejamentoControle): OutlookCardDados[] {
  const card = (chave: string, titulo: string, campo: Categoria, ausente = false) =>
    montarCard(
      chave,
      titulo,
      ausente
        ? null
        : {
            outlook: totais.outlook[campo],
            naoComprometido: totais.naoComprometido[campo],
            comprometido: totais.comprometido[campo],
          },
      {
        nc: totais.percentuais.naoComprometido[campo],
        cp: totais.percentuais.comprometido[campo],
      },
    );

  return [
    card("total-obra", OUTLOOK_CARD_TOTAL_OBRA, "total"),
    card("servicos", OUTLOOK_CARD_SERVICOS, "servicos"),
    card("materiais", OUTLOOK_CARD_MATERIAIS, "materiais"),
    card(
      "facilidades",
      OUTLOOK_CARD_FACILIDADES,
      "facilidades",
      !linhas.some((linha) => linha.natureza === "FACILIDADES"),
    ),
  ];
}
